import { SHOPIFY_STORE_DOMAIN, SHOPIFY_STOREFRONT_API_TOKEN } from '@portal/config/base';
import { LOCALE_TO_SHOPIFY_COUNTRY_CODE, LocaleEnum, LocaleStore } from '@portal/schema/locale';
import { shopifyAdminRequest } from '@portal/state/shopifyAdmin/shopifyAdminRequest';
import { StoreCode } from '../../../schema/storeCode';
import { getProductsByIdsQuery } from '../queries/productById';
import { getProductVariantQuery, getProductVariantsByIdsQuery } from '../queries/variantById';
import type { ShopifyProductVariant, ShopifyProductVariantsByIDsOperation } from '../types';
import { removeEdgesAndNodes, shopifyFetch } from '../utils';

export const loadShopifyProductVariantsByIDs = async (
  locale: LocaleEnum,
  ids: string[]
): Promise<ShopifyProductVariant[]> => {
  const queryIds = ids.reduce<string[]>((acc, i) => {
    let result = i;
    // Grab the last part of the id.
    if (typeof i === 'string') {
      if (i.startsWith('gid://')) {
        const paths = i.split('/');
        result = paths[paths.length - 1];
      }
    }

    const numericId = Number(result);
    if (!Number.isNaN(numericId)) {
      result = `(id:${result})`;
      if (!acc.includes(result)) {
        acc.push(result);
      }
    }
    return acc;
  }, []);

  if (!queryIds.length) {
    // eslint-disable-next-line no-console
    console.warn('No variant IDs provided');
    return [];
  }

  const res = await shopifyAdminRequest<
    ShopifyProductVariantsByIDsOperation['data'],
    ShopifyProductVariantsByIDsOperation['variables']
  >({
    document: getProductVariantsByIdsQuery,
    storeCode: LocaleStore[locale],
    variables: {
      query: queryIds.join(' OR '),
      countryCode: LOCALE_TO_SHOPIFY_COUNTRY_CODE[locale]
    }
  });

  ids.forEach((id) => {
    const shopifyVariant = res.productVariants.edges.find((edge) => edge.node.id === id);
    if (!shopifyVariant) {
      // eslint-disable-next-line no-console
      console.warn(`No Shopify variant found for ID: ${id} ${locale}`);
    }
  });

  return removeEdgesAndNodes(res.productVariants);
};

/**
 * Fetch a single product variant from Shopify using the Storefront API
 */
export const loadShopifyProductVariantById = async (
  storeCode: StoreCode,
  variantId: string
): Promise<any> => {
  // Ensure the variant ID is in the correct GID format
  const formattedVariantId = variantId.startsWith('gid://shopify/ProductVariant/')
    ? variantId
    : `gid://shopify/ProductVariant/${variantId}`;

  const res = await shopifyFetch<any>({
    query: getProductVariantQuery,
    shopifyDomain: SHOPIFY_STORE_DOMAIN[storeCode],
    accessToken: SHOPIFY_STOREFRONT_API_TOKEN[storeCode],
    variables: { id: formattedVariantId }
  });

  return res.body.data.node;
};

/**
 * Fetch multiple products by IDs and return their first variants in a single API call
 */
export const loadShopifyProductsFirstVariantsByIds = async (
  storeCode: StoreCode,
  productIds: string[]
): Promise<Array<{ productId: string; variant: any } | null>> => {
  if (!productIds.length) {
    return [];
  }

  // Ensure all product IDs are in the correct GID format
  const formattedProductIds = productIds.map((productId) =>
    productId.startsWith('gid://shopify/Product/')
      ? productId
      : `gid://shopify/Product/${productId}`
  );

  const res = await shopifyFetch<any>({
    query: getProductsByIdsQuery,
    shopifyDomain: SHOPIFY_STORE_DOMAIN[storeCode],
    accessToken: SHOPIFY_STOREFRONT_API_TOKEN[storeCode],
    variables: { ids: formattedProductIds }
  });

  const products = res.body.data.nodes || [];

  return products.map((product: any) => {
    if (!product || !product.variants?.edges?.length) {
      return null;
    }

    const firstVariant = product.variants.edges[0].node;
    return {
      productId: product.id,
      variant: firstVariant
    };
  });
};

export const checkVariantAvailability = async (
  variantId: string,
  storeCode: StoreCode
): Promise<{ availableForSale: boolean; variant: any }> => {
  const formattedVariantId = variantId.startsWith('gid://shopify/ProductVariant/')
    ? variantId
    : `gid://shopify/ProductVariant/${variantId}`;

  const res = await shopifyFetch<any>({
    query: getProductVariantQuery,
    shopifyDomain: SHOPIFY_STORE_DOMAIN[storeCode],
    accessToken: SHOPIFY_STOREFRONT_API_TOKEN[storeCode],
    variables: { id: formattedVariantId }
  });

  const variant = res.body.data.node;
  return {
    availableForSale: variant.availableForSale,
    variant
  };
};
